import React, { useEffect, useState } from "react";
import { Homework } from "../../../../../Glimpse/GlimpseV2/Homework";
import { FailedTasksModal } from "../../../../../Glimpse/GlimpseV2/FailedTasksModal";
import { SvgIcons } from "../../../../../SharedComponents/SvgIcons/SvgIcons";
import { isAiEnabledSubject } from "../../../../AssignAiTestFlow/utils/constants";
import InboxTabs from "../../../../UI/InboxTabs";
import useHomeworkModal from "./useHomeworkModal";
import styles from "./style.module.css";
import Tooltip from "../../../../UI/Tooltip";

const TABS = ["Assign homework", "Review homework"];


const ReviewHomeworkCard = ({ item, onClick }) => {
  const typeLabels = { exercise: "Exercise", link: "Link", upload: "Upload", file: "File" };

  return (
    <div className={styles.reviewCard} onClick={onClick}>
      <div className={styles.reviewCardHeader}>
        <span className={styles.reviewTypeBadge}>
          <SvgIcons name="file_icon" width={12} height={12} style={{ color: "#3377FF" }} />
          {typeLabels[item.type] || item.type || "Exercise"}
        </span>
        <span className={styles.reviewDate}>
          <SvgIcons name="calendar_icon" width={12} height={12} style={{ color: "#858585" }} />
          Assigned on {item.assignedDate}
        </span>
        <SvgIcons name="redirection_arrow_circle" width={18} height={18} style={{ marginLeft: "auto", flexShrink: 0 }} />
      </div>
      <div className={styles.reviewDescriptionRow}>
        <Tooltip text={item.tooltipText} position="top">
          <p className={styles.reviewDescription}>{item.displayText}</p>
        </Tooltip>
      </div>
    </div>
  );
};

/**
 * HomeworkModal — modal with Assign/Review homework tabs.
 * Assign tab reuses the existing Homework component from GlimpseV2.
 * Review tab shows pending homework items in a 2-column grid.
 */
const HomeworkModal = ({ open, student, subject, onClose }) => {
  const [activeTab, setActiveTab] = useState(TABS[0]);

  const {
    exerciseList,
    selectedExercises,
    urlArray,
    uploadedFiles,
    urlValue,
    urlTitle,
    selectedAiHomework,
    failedTasksPopup,
    reviewItems,
    reviewCount,
    isLoadingReview,
    handlers,
  } = useHomeworkModal(student, open, subject);

  // AI homework is only offered for the noah2 subjects — matches the Assign-test flow.
  const showAiHomework = isAiEnabledSubject(subject);

  useEffect(() => {
    if (document.body && open) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      if (document.body) {
        document.body.style.overflow = "auto";
      }
    };
  }, [open]);

  useEffect(() => {
   setActiveTab(TABS[0]);
  }, [open]);

  if (!open || !student) return null;

  const handleViewSPR = () => {
    if (student.studentId) {
      window.open(
        `${process.env.REACT_APP_SPR}/${student.studentId}/${student.batchId}/${student.courseId}`
      );
    }
  };


  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <img
              src={student.profileUrl || ""}
              alt={student.studentName}
              className={styles.headerAvatar}
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <div className={styles.headerInfo}>
              <span className={styles.headerName}>{student.studentName}</span>
              <span className={styles.headerBatch}>{student.batchId || student.course}</span>
            </div>
          </div>
          <div className={styles.headerRight}>
            <button className={styles.sprBtn} onClick={handleViewSPR}>
              View SPR <SvgIcons name="diagonal_arrow" width={10} height={10} />
            </button>
            <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
              <SvgIcons name="cross_btn" width={18} height={18} />
            </button>
          </div>
        </div>

        <div className={styles.tabWrapper} style={{ flex: 1 }}>
          <InboxTabs
            tabs={TABS}
            selectedTab={activeTab}
            onSelectTab={setActiveTab}
            counts={{ [TABS[0]]: 0, [TABS[1]]: reviewCount }}
          />

          <div className={`${styles.tabContent} ${activeTab === TABS[0] ? styles.tabContentAssign : ""}`}>
            {activeTab === TABS[0] && (
              <div className={styles.assignTabInner}>
                <Homework
                  exerciseList={exerciseList}
                  selectedExercises={selectedExercises}
                  urlArray={urlArray}
                  uploadedFiles={uploadedFiles}
                  urlValue={urlValue}
                  urlTitle={urlTitle}
                  onSearchExercise={handlers.handleSearchExercise}
                  onSelectExercise={handlers.handleSelectExercise}
                  onRemoveExercise={handlers.handleRemoveExercise}
                  onUrlValueChange={handlers.handleUrlValueChange}
                  onUrlTitleChange={handlers.handleUrlTitleChange}
                  onAddUrl={handlers.handleAddUrl}
                  onRemoveUrl={handlers.handleRemoveUrl}
                  onFileChange={handlers.handleFileChange}
                  onRemoveFile={handlers.handleRemoveFile}
                  isFromDashboard={true}
                  aiHwEnabled={showAiHomework}
                  courseSection={subject}
                  topicIds={[]}
                  selectedAiHomework={selectedAiHomework}
                  onAddAiHomework={handlers.handleAddAiHomework}
                  onRemoveAiHomework={handlers.handleRemoveAiHomework}
                  onUpdateAiHomework={handlers.handleUpdateAiHomework}
                />
              </div>
            )}

            {activeTab === TABS[1] && (
              <div className={styles.reviewTabInner}>
                {isLoadingReview ? (
                  <div className={styles.emptyReview}>Loading...</div>
                ) : reviewItems.length === 0 ? (
                  <div className={styles.emptyReview}>No homework assigned yet</div>
                ) : (
                  <div className={styles.reviewGrid}>
                    {reviewItems.map((item, i) => (
                      <ReviewHomeworkCard
                        key={item.homeworkId || i}
                        item={item}
                        onClick={() => handlers.handleCardClick(item)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {activeTab === TABS[0] && (
          <button
            className={styles.assignBtn}
            onClick={handlers.handleAssign}
            disabled={handlers.isAssigning}
          >
            {handlers.isAssigning ? "Assigning..." : "Assign"}
          </button>
        )}
      </div>

      {failedTasksPopup && (
        <FailedTasksModal
          items={failedTasksPopup?.items}
          onClose={handlers.handleFailedTasksConfirm}
          onConfirm={handlers.handleFailedTasksConfirm}
        />
      )}
    </div>
  );
};

export default HomeworkModal;
